import api from './api';
import {
  AdminAssignableOption,
  AdminBankAccount,
  AdminDashboardData,
  AdminFinancialOverview,
  AdminKanbanBoard,
  AdminKanbanTask,
  AdminMetrics,
  AdminOwnerContract,
  AdminOwnerErpDetail,
  AdminOwnerFinancialProfile,
  AdminPayable,
  AdminReceivable,
  AdminReferral,
  AdminUser,
  ErrorLog,
  OwnersPageResponse,
  PaymentRecord,
  ReferralStats,
  RevenueForecast
} from '../types/admin/AdminTypes';

class AdminService {
  private readonly baseUrl = '/api/admin';
  private readonly erpBaseUrl = '/api/admin/erp';

  async getDashboardData(): Promise<AdminDashboardData> {
    const response = await api.get(`${this.baseUrl}/dashboard`);
    return response.data;
  }

  async getMetrics(): Promise<AdminMetrics> {
    const response = await api.get(`${this.baseUrl}/metrics`);
    return response.data;
  }

  async getUsers(page = 1, limit = 20, filters?: {
    status?: string;
    plan?: string;
    search?: string;
  }): Promise<{ users: AdminUser[]; total: number; pages: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.plan && { plan: filters.plan }),
      ...(filters?.search && { search: filters.search }),
    });
    const response = await api.get(`${this.baseUrl}/users?${params}`);
    return response.data;
  }

  async updateUserStatus(userId: string, status: 'active' | 'inactive' | 'suspended'): Promise<AdminUser> {
    const response = await api.put(`${this.baseUrl}/users/${userId}/status`, { status });
    return response.data;
  }

  async getErrorLogs(page = 1, limit = 50): Promise<{ errors: ErrorLog[]; total: number }> {
    const response = await api.get(`${this.baseUrl}/errors?page=${page}&limit=${limit}`);
    return response.data;
  }

  async resolveError(errorId: string): Promise<void> {
    await api.put(`${this.baseUrl}/errors/${errorId}/resolve`);
  }

  async getPayments(page = 1, limit = 50, filters?: {
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ payments: PaymentRecord[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.startDate && { startDate: filters.startDate }),
      ...(filters?.endDate && { endDate: filters.endDate }),
    });
    const response = await api.get(`${this.baseUrl}/payments?${params}`);
    return response.data;
  }

  async getReferralStats(): Promise<ReferralStats> {
    const response = await api.get(`${this.baseUrl}/referrals/stats`);
    return response.data;
  }

  async getReferrals(page = 1, limit = 50, filters?: {
    status?: string;
    search?: string;
  }): Promise<{ referrals: AdminReferral[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.search && { search: filters.search }),
    });
    const response = await api.get(`${this.baseUrl}/referrals?${params}`);
    return response.data;
  }

  async getRevenueForecast(months = 6): Promise<RevenueForecast[]> {
    const response = await api.get(`${this.baseUrl}/forecast?months=${months}`);
    return response.data;
  }

  async grantFreeAccess(userId: string, reason: string): Promise<void> {
    await api.post(`${this.baseUrl}/users/${userId}/free-access`, { reason });
  }

  async revokeFreeAccess(userId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/users/${userId}/free-access`);
  }

  async resetPassword(userId: string, novaSenha: string): Promise<void> {
    await api.post(`${this.baseUrl}/users/${userId}/reset-password`, { novaSenha });
  }

  async getKanbanBoard(): Promise<AdminKanbanBoard> {
    const response = await api.get(`${this.baseUrl}/kanban`);
    return response.data;
  }

  async searchKanbanAdmins(search?: string, limit = 10): Promise<AdminAssignableOption[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      ...(search ? { search } : {})
    });
    const response = await api.get(`${this.baseUrl}/kanban/admins?${params}`);
    return response.data;
  }

  async createKanbanTask(payload: {
    title: string;
    description?: string;
    type: string;
    status?: string;
    responsibleAdminId?: string;
  }): Promise<AdminKanbanTask> {
    const response = await api.post(`${this.baseUrl}/kanban/tasks`, payload);
    return response.data;
  }

  async updateKanbanTask(taskId: string, payload: {
    title: string;
    description?: string;
    type: string;
    status: string;
    responsibleAdminId?: string;
  }): Promise<AdminKanbanTask> {
    const response = await api.put(`${this.baseUrl}/kanban/tasks/${taskId}`, payload);
    return response.data;
  }

  async moveKanbanTask(taskId: string, payload: {
    type: string;
    destinationIndex: number;
  }): Promise<AdminKanbanTask> {
    const response = await api.put(`${this.baseUrl}/kanban/tasks/${taskId}/move`, payload);
    return response.data;
  }

  async deleteKanbanTask(taskId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/kanban/tasks/${taskId}`);
  }

  async getOwners(page = 1, limit = 25, search?: string): Promise<OwnersPageResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search ? { search } : {})
    });
    const response = await api.get(`${this.erpBaseUrl}/owners?${params}`);
    return response.data;
  }

  async getOwnerDetail(ownerId: string): Promise<AdminOwnerErpDetail> {
    const response = await api.get(`${this.erpBaseUrl}/owners/${ownerId}`);
    return response.data;
  }

  async updateOwnerProfile(ownerId: string, payload: {
    billingCategory: string;
    manualBillingMode?: string;
    notes?: string;
  }): Promise<AdminOwnerFinancialProfile> {
    const response = await api.put(`${this.erpBaseUrl}/owners/${ownerId}/profile`, payload);
    return response.data;
  }

  async getContracts(filters?: {
    ownerId?: string;
    status?: string;
    search?: string;
  }): Promise<AdminOwnerContract[]> {
    const params = new URLSearchParams({
      ...(filters?.ownerId ? { ownerId: filters.ownerId } : {}),
      ...(filters?.status ? { status: filters.status } : {}),
      ...(filters?.search ? { search: filters.search } : {})
    });
    const response = await api.get(`${this.erpBaseUrl}/contracts${params.toString() ? `?${params}` : ''}`);
    return response.data;
  }

  async createContract(payload: {
    ownerId: string;
    title: string;
    contractReference?: string;
    status?: string;
    manualBillingMode: string;
    billingMethod: string;
    startDate: string;
    endDate: string;
    firstDueDate: string;
    installmentCount: number;
    totalAmount: number;
    notes?: string;
    regenerateReceivables?: boolean;
  }): Promise<AdminOwnerContract> {
    const response = await api.post(`${this.erpBaseUrl}/contracts`, payload);
    return response.data;
  }

  async updateContract(contractId: string, payload: {
    ownerId: string;
    title: string;
    contractReference?: string;
    status?: string;
    manualBillingMode: string;
    billingMethod: string;
    startDate: string;
    endDate: string;
    firstDueDate: string;
    installmentCount: number;
    totalAmount: number;
    notes?: string;
    regenerateReceivables?: boolean;
  }): Promise<AdminOwnerContract> {
    const response = await api.put(`${this.erpBaseUrl}/contracts/${contractId}`, payload);
    return response.data;
  }

  async getReceivables(filters?: {
    ownerId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AdminReceivable[]> {
    const params = new URLSearchParams({
      ...(filters?.ownerId ? { ownerId: filters.ownerId } : {}),
      ...(filters?.status ? { status: filters.status } : {}),
      ...(filters?.startDate ? { startDate: filters.startDate } : {}),
      ...(filters?.endDate ? { endDate: filters.endDate } : {})
    });
    const response = await api.get(`${this.erpBaseUrl}/receivables${params.toString() ? `?${params}` : ''}`);
    return response.data;
  }

  async createReceivable(payload: {
    ownerId: string;
    contractId?: string;
    description: string;
    installmentNumber?: number;
    dueDate: string;
    amount: number;
    status?: string;
    paymentMethod?: string;
    bankAccountId?: string;
    notes?: string;
  }): Promise<AdminReceivable> {
    const response = await api.post(`${this.erpBaseUrl}/receivables`, payload);
    return response.data;
  }

  async updateReceivable(receivableId: string, payload: {
    ownerId: string;
    contractId?: string;
    description: string;
    installmentNumber?: number;
    dueDate: string;
    amount: number;
    status?: string;
    paymentMethod?: string;
    bankAccountId?: string;
    notes?: string;
  }): Promise<AdminReceivable> {
    const response = await api.put(`${this.erpBaseUrl}/receivables/${receivableId}`, payload);
    return response.data;
  }

  async receiveReceivable(receivableId: string, payload: {
    receivedAmount: number;
    receivedAt?: string;
    paymentMethod?: string;
    bankAccountId?: string;
    notes?: string;
  }): Promise<AdminReceivable> {
    const response = await api.post(`${this.erpBaseUrl}/receivables/${receivableId}/receive`, payload);
    return response.data;
  }

  async getPayables(filters?: {
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AdminPayable[]> {
    const params = new URLSearchParams({
      ...(filters?.status ? { status: filters.status } : {}),
      ...(filters?.startDate ? { startDate: filters.startDate } : {}),
      ...(filters?.endDate ? { endDate: filters.endDate } : {})
    });
    const response = await api.get(`${this.erpBaseUrl}/payables${params.toString() ? `?${params}` : ''}`);
    return response.data;
  }

  async createPayable(payload: {
    description: string;
    expenseCategory?: string;
    dueDate: string;
    amount: number;
    status?: string;
    paymentMethod?: string;
    bankAccountId?: string;
    notes?: string;
  }): Promise<AdminPayable> {
    const response = await api.post(`${this.erpBaseUrl}/payables`, payload);
    return response.data;
  }

  async updatePayable(payableId: string, payload: {
    description: string;
    expenseCategory?: string;
    dueDate: string;
    amount: number;
    status?: string;
    paymentMethod?: string;
    bankAccountId?: string;
    notes?: string;
  }): Promise<AdminPayable> {
    const response = await api.put(`${this.erpBaseUrl}/payables/${payableId}`, payload);
    return response.data;
  }

  async payPayable(payableId: string, payload: {
    paidAmount: number;
    paidAt?: string;
    paymentMethod?: string;
    bankAccountId?: string;
    notes?: string;
  }): Promise<AdminPayable> {
    const response = await api.post(`${this.erpBaseUrl}/payables/${payableId}/pay`, payload);
    return response.data;
  }

  async getBankAccounts(): Promise<AdminBankAccount[]> {
    const response = await api.get(`${this.erpBaseUrl}/bank-accounts`);
    return response.data;
  }

  async createBankAccount(payload: {
    name: string;
    bankName: string;
    agency?: string;
    accountNumber?: string;
    initialBalance: number;
    active?: boolean;
    notes?: string;
  }): Promise<AdminBankAccount> {
    const response = await api.post(`${this.erpBaseUrl}/bank-accounts`, payload);
    return response.data;
  }

  async updateBankAccount(bankAccountId: string, payload: {
    name: string;
    bankName: string;
    agency?: string;
    accountNumber?: string;
    initialBalance: number;
    active?: boolean;
    notes?: string;
  }): Promise<AdminBankAccount> {
    const response = await api.put(`${this.erpBaseUrl}/bank-accounts/${bankAccountId}`, payload);
    return response.data;
  }

  async getFinancialSummary(startDate?: string, endDate?: string): Promise<AdminFinancialOverview> {
    const params = new URLSearchParams({
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {})
    });
    const response = await api.get(`${this.erpBaseUrl}/reports/financial-summary${params.toString() ? `?${params}` : ''}`);
    return response.data;
  }
}

export const adminService = new AdminService();
export default adminService;
